import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/AuthContext";
import { submitContactMessage } from "@/lib/api";
import { toast } from "sonner";

/**
 * Floating bottom-right "Help / Feedback" pill. Opens a compact contact
 * drawer that posts to the existing `/api/contact` endpoint. We hide it
 * on `/auth`, `/disclaimer`, and any "/preview"-style page where it would
 * compete with the page's own CTA. Reads the current user from AuthContext
 * to pre-fill name + email when they're signed in.
 */
export default function HelpPill() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Routes where the pill would overlap critical UI / forms.
  const hiddenOn = ["/auth", "/disclaimer"];
  if (hiddenOn.some((p) => pathname.startsWith(p))) return null;

  const openDrawer = () => {
    if (user && user !== false) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContactMessage({ name: name.trim(), email: email.trim(), message: message.trim() });
      toast.success("Thanks — we'll get back to you within 1 business day.");
      setOpen(false);
      setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Pill — only shown when drawer is closed */}
      {!open && (
        <button
          type="button"
          data-testid="help-pill-trigger"
          onClick={openDrawer}
          className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-[#1C1C1C] text-white shadow-lg hover:bg-[#2A2A2E] transition-colors text-[13px] font-medium"
          aria-label="Open help & feedback"
        >
          <LifeBuoy className="w-4 h-4" />
          Help / Feedback
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          data-testid="help-pill-drawer"
          className="fixed bottom-4 right-4 z-50 w-[min(360px,calc(100vw-2rem))] fc-card p-4 shadow-2xl border border-black/[0.08]"
          role="dialog"
          aria-labelledby="help-pill-title"
        >
          <div className="flex items-center justify-between mb-3">
            <div id="help-pill-title" className="fc-heading text-[#1C1C1C] text-base font-medium inline-flex items-center gap-2">
              <LifeBuoy className="w-4 h-4 text-sage-700" /> Help / Feedback
            </div>
            <button
              type="button"
              data-testid="help-pill-close"
              onClick={() => setOpen(false)}
              className="text-[#78716C] hover:text-[#1C1C1C] w-7 h-7 grid place-items-center rounded-md hover:bg-black/[0.04]"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[12px] text-[#78716C] mb-3 leading-snug">
            Spotted a bug, have a feature idea, or stuck on a project? Drop us a line.
          </p>
          <form onSubmit={onSubmit} className="space-y-2.5">
            <Input
              data-testid="help-pill-name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="fc-input h-9 rounded-md text-sm"
              required
            />
            <Input
              data-testid="help-pill-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="fc-input h-9 rounded-md text-sm"
              required
            />
            <Textarea
              data-testid="help-pill-message"
              placeholder="What's on your mind?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="fc-input rounded-md text-sm resize-none"
              required
            />
            <Button
              type="submit"
              data-testid="help-pill-submit"
              disabled={submitting}
              className="w-full bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-9 text-sm rounded-md"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {submitting ? "Sending…" : "Send message"}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
